import { View, StyleSheet, Pressable } from "react-native";
import { useState } from "react";
import CustomText from "../../common/general/CustomText/CustomText";
import { File } from "@phosphor-icons/react/dist/csr/File";
import { CheckCircle } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { Trash } from "@phosphor-icons/react/dist/csr/Trash";

export type DocumentStatus = "ready" | "uploading" | "processing" | "failed" | "deleting";

export interface DocumentData {
  id: string;
  documentId?: string;
  fileId?: string;
  filename: string;
  size: string;
  uploadedDate: string;
  status: DocumentStatus;
  statusText?: string;
}

interface AdminCompanionDocumentCardProps {
  document: DocumentData;
  onDelete?: (id: string) => void;
}

function AdminCompanionDocumentCard({ document, onDelete }: AdminCompanionDocumentCardProps) {
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  const isProcessing =
    document.status === "uploading" || document.status === "processing" || document.status === "deleting";

  const getStatusColor = () => {
    switch (document.status) {
      case "ready":
        return { bg: "#F0FDF4", text: "#16A34A", iconColor: "#22C55E" };
      case "processing":
        return { bg: "#F5F3FF", text: "#7C3AED", iconColor: "#8B5CF6" };
      case "uploading":
        return { bg: "#EFF6FF", text: "#2563EB", iconColor: "#3B82F6" };
      case "failed":
        return { bg: "#FEF2F2", text: "#B91C1C", iconColor: "#EF4444" };
      case "deleting":
        return { bg: "#F3F4F6", text: "#6B7280", iconColor: "#6B7280" };
      default:
        return { bg: "#F0FDF4", text: "#16A34A", iconColor: "#22C55E" };
    }
  };

  const statusColors = getStatusColor();

  const getStatusIcon = () => {
    if (document.status === "ready") {
      return <CheckCircle size={16} color={statusColors.iconColor} weight="regular" />;
    }
    return null;
  };

  const getStatusLabel = () => {
    if (document.statusText) {
      return document.statusText;
    }
    switch (document.status) {
      case "ready":
        return "Ready";
      case "processing":
        return "Processing...";
      case "uploading":
        return "Uploading...";
      case "failed":
        return "Failed";
      case "deleting":
        return "Deleting...";
      default:
        return "Ready";
    }
  };

  return (
    <View style={styles.card}>
      <View style={styles.iconContainer}>
        <File size={20} color="#9CA3AF" weight="regular" />
      </View>

      <View style={styles.contentContainer}>
        <CustomText
          text={document.filename}
          size="s"
          weight="medium"
          useTranslationText={false}
          style={styles.filename}
          numberOfLines={1}
        />
        <CustomText
          text={`${document.size} • Uploaded ${document.uploadedDate}`}
          size="xs"
          useTranslationText={false}
          style={styles.metadata}
        />
      </View>

      <View style={[styles.statusBadge, { backgroundColor: statusColors.bg }]}>
        {getStatusIcon() && (
          <>
            {getStatusIcon()}
            <View style={{ width: 6 }} />
          </>
        )}
        <CustomText
          text={getStatusLabel()}
          size="xs"
          weight="medium"
          useTranslationText={false}
          style={{ color: statusColors.text }}
        />
      </View>

      <Pressable
        style={[
          styles.deleteButton,
          isDeleteHovered && !isProcessing && styles.deleteButtonHovered,
          isProcessing && styles.deleteButtonDisabled,
        ]}
        onPress={() => !isProcessing && onDelete?.(document.id)}
        onHoverIn={() => setIsDeleteHovered(true)}
        onHoverOut={() => setIsDeleteHovered(false)}
        disabled={isProcessing}
        aria-label={`Delete ${document.filename}`}>
        <Trash size={16} color={isProcessing ? "#D1D5DB" : "#9CA3AF"} weight="regular" />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  contentContainer: {
    flex: 1,
    minWidth: 0,
  },
  filename: {
    color: "#111827",
  },
  metadata: {
    color: "#6B7280",
    marginTop: 2,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 9999,
  },
  deleteButton: {
    padding: 8,
    borderRadius: 8,
  },
  deleteButtonHovered: {
    backgroundColor: "#FEF2F2",
  },
  deleteButtonDisabled: {
    opacity: 0.5,
  },
});

export default AdminCompanionDocumentCard;
